import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { splitbee } from '../utils/splitbee'
import { useCookieConsent } from './CookieConsent'
import { useUserContext } from './UserProvider'

export const ConsentAwareTracking = () => {
  const router = useRouter()
  const { preferences } = useCookieConsent()
  const ctx = useUserContext()
  const isImpersonated = Boolean(ctx?.user?.impersonator)
  const isDevelopment =
    process.env.NODE_ENV === 'development' || process.env.STAGE === 'dev'

  useEffect(() => {
    // Don't track for impersonated users
    if (isImpersonated || router.asPath.startsWith('/login/callback')) {
      return
    }

    // Only track Google Ads clicks if marketing cookies are allowed
    if (preferences?.marketing) {
      const gclid = router.query.gclid
      if (gclid) {
        splitbee.track('Visit via ad-click', {
          source: 'Google',
          gclid: Array.isArray(gclid) ? gclid[0] : gclid,
        })
      }
    }
  }, [
    router.query.gclid,
    preferences?.marketing,
    isImpersonated,
    router.asPath,
  ])

  // Track page views only if analytics cookies are allowed
  useEffect(() => {
    // Don't track for impersonated users
    if (isImpersonated || router.asPath.startsWith('/login/callback')) {
      return
    }

    if (preferences?.analytics) {
      // Splitbee automatically tracks page views, but we could add custom tracking here
      // For example, trigger GTM events for page views
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'page_view', {
          page_path: router.asPath,
        })
      }
    }
  }, [router.asPath, preferences?.analytics, isImpersonated])

  // Log consent status for debugging
  useEffect(() => {
    if (isDevelopment) {
      if (isImpersonated) {
        console.log('🎭 Impersonator detected - Cookie tracking disabled')
      } else if (preferences) {
        console.log('🍪 Cookie Consent Status for DEV:', {
          analytics: preferences.analytics ? '✅ Enabled' : '❌ Disabled',
          marketing: preferences.marketing ? '✅ Enabled' : '❌ Disabled',
          necessary: '✅ Always Enabled',
        })
      }
    }
  }, [preferences, isImpersonated])

  return null
}
